import { ActionDto, ChanceNodeDto, ChanceNodeLogDto, TriggerDto } from "@api";
import { createFeatureSelector, createSelector, DefaultProjectorFn, MemoizedSelector } from "@ngrx/store";
import { sortByCreatedAt } from "src/app/utils/sort-by-date";
import { sortByName } from "src/app/utils/sort-by-name";
import { RootState } from "..";
import { selectScenarioId } from "../router/selectors";
import { ScenarioState } from "./state";

const getState = createFeatureSelector<ScenarioState>("scenario");

export const getTriggers = createSelector(getState, state => state.triggers);

export const getTrigger = (triggerId: string): MemoizedSelector<RootState, TriggerDto, DefaultProjectorFn<TriggerDto>> =>
  createSelector(getTriggers, triggers => triggers.find(trigger => trigger.id === triggerId));

export const getChanceNodes = createSelector(getState, state => Object.values(state.chanceNodes));

export const getActions = createSelector(getState, state => state.actions);

export const getAction = (actionId: string): MemoizedSelector<RootState, ActionDto, DefaultProjectorFn<ActionDto>> =>
  createSelector(getActions, actions => actions.find(action => action.id === actionId));

export const getLoading = createSelector(getState, state => state.loading);

export const getScenarios = createSelector(getChanceNodes, getTriggers, (chanceNodes, triggers) =>
  chanceNodes
    .filter(chanceNode => {
      const trigger = triggers.find(t => t.id === chanceNode.triggerId);
      return trigger?.type !== TriggerDto.TypeEnum.Scenario;
    })
    .sort(sortByName),
);

export const getScenario = createSelector(selectScenarioId, getState, (scenarioId, state) => state.chanceNodes[scenarioId]);

export const getScenarioRuns = createSelector(selectScenarioId, getState, (scenarioId, state) =>
  [...(state.runs[scenarioId]?.data || [])].sort((a, b) => +b.runId - +a.runId),
);

export const getScenarioHistoryCurrentPage = createSelector(
  selectScenarioId,
  getState,
  (scenarioId, state) => state.runs[scenarioId]?.page,
);

export const getScenarioHistoryTotalPages = createSelector(
  selectScenarioId,
  getState,
  (scenarioId, state) => state.runs[scenarioId]?.pageCount,
);

export const getScenarioTrigger = createSelector(selectScenarioId, getState, (scenarioId, state) =>
  state.triggers?.find(trigger => trigger.id === state.chanceNodes[scenarioId]?.triggerId),
);

export const getScenarioRunLogs = (
  runId: number,
): MemoizedSelector<RootState, ChanceNodeLogDto[], DefaultProjectorFn<ChanceNodeLogDto[]>> =>
  createSelector(
    selectScenarioId,
    getState,
    (scenarioId, state) =>
      state.logs[scenarioId] &&
      state.logs[scenarioId][runId] &&
      [...state.logs[scenarioId][runId].data].sort((a, b) => sortByCreatedAt(a, b, "asc")),
  );

export const getScenarioRunLogsCurrentPage = (runId: number): MemoizedSelector<RootState, number, DefaultProjectorFn<number>> =>
  createSelector(
    selectScenarioId,
    getState,
    (scenarioId, state) => state.logs[scenarioId] && state.logs[scenarioId][runId] && state.logs[scenarioId][runId].page,
  );

export const getScenarioRunLogsTotalPages = (runId: number): MemoizedSelector<RootState, number, DefaultProjectorFn<number>> =>
  createSelector(
    selectScenarioId,
    getState,
    (scenarioId, state) => state.logs[scenarioId] && state.logs[scenarioId][runId] && state.logs[scenarioId][runId].pageCount,
  );

export const getScenarioById = (scenarioId: string): MemoizedSelector<RootState, ChanceNodeDto, DefaultProjectorFn<ChanceNodeDto>> =>
  createSelector(getState, state => state.chanceNodes[scenarioId]);
